import { useEffect, useState } from 'react';
import { fetchApprovalConfig } from '@/shared/api/approval.config.api';
import { env } from '@/shared/config/env';
import { EVENT } from '@/shared/config/event.config';
import { applyThemeColor } from '@/shared/config/theme';

export interface EventIdentity {
  /** Nome do evento, do banco quando disponível. */
  title: string;
  /** Segunda linha: a edição, que só existe nos ajustes locais. */
  edition: string;
  /** "Nome Edição", para uso no meio de uma frase. */
  fullTitle: string;
  /**
   * `true` enquanto a consulta ao banco não terminou. Vira `false` também quando
   * ela falha — aí vale a reserva, e quem espera não pode esperar para sempre.
   */
  loading: boolean;
}

/**
 * Identificação do evento para as telas **fora** do portão de autenticação.
 *
 * O título vem do banco, do mesmo `GET /approval/config` que o painel usa — a rota não
 * exige sessão, o que é o que permite a tela de login se identificar. O que está em
 * `panel.settings.json` é reserva: vale até a resposta chegar, e quando ela falha.
 *
 * Falha não vira erro na tela: segue valendo a reserva. Quem quiser esperar pelo nome
 * do banco usa `loading` (a tela de login espera, para não trocar o nome debaixo de
 * quem já está lendo) — e a espera acaba tanto no acerto quanto no erro.
 *
 * Dentro do portão o título vem do `EventConfigProvider`, que já tem o documento
 * inteiro em mãos — este hook não é para lá.
 */
export function useEventIdentity(): EventIdentity {
  const [title, setTitle] = useState(EVENT.title);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    fetchApprovalConfig(env.miceEventId, controller.signal)
      .then(document => {
        if (controller.signal.aborted) return;
        // A cor da marca sai do mesmo documento, e é aplicada aqui porque a tela
        // de login está fora do portão: o `EventConfigProvider` só monta depois
        // da sessão, e até lá o painel ficaria com a cor padrão.
        applyThemeColor(document?.themeColor);
        const fromDatabase = document?.title?.trim();
        if (!fromDatabase) return;
        setTitle(fromDatabase);
      })
      // Falha aqui não vira erro na tela: segue valendo a reserva.
      .catch(() => {})
      // No acerto e no erro: a espera termina de qualquer forma. Abortado não conta —
      // o componente saiu, e setar estado aqui seria escrever em algo desmontado.
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  return {
    title,
    edition: EVENT.edition,
    fullTitle: [title, EVENT.edition].filter(Boolean).join(' '),
    loading,
  };
}
